import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, X, Clock, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import type { WorkflowInstanceView, WorkflowInstanceStepView } from '../../models/attendance';
import { StepStatus } from '../../models/attendance';

// --- Step status resolution ---

type ResolvedStepState = 'completed' | 'current' | 'upcoming' | 'rejected' | 'escalated' | 'skipped';

function resolveStepState(
  step: WorkflowInstanceStepView,
  currentStepOrder: number
): ResolvedStepState {
  switch (step.status) {
    case StepStatus.Approved:
    case StepStatus.AutoApproved:
      return 'completed';
    case StepStatus.Rejected:
      return 'rejected';
    case StepStatus.Skipped:
      return 'skipped';
    case StepStatus.Pending:
    default:
      return step.stepOrder === currentStepOrder ? 'current' : 'upcoming';
  }
}

// --- CVA variants ---

const trackerVariants = cva('flex', {
  variants: {
    variant: {
      detailed: 'flex-col gap-3',
      compact: 'flex-row items-center gap-1',
      mini: 'flex-row items-center gap-0.5',
    },
  },
  defaultVariants: {
    variant: 'detailed',
  },
});

const stepIndicatorVariants = cva(
  'flex items-center justify-center rounded-full shrink-0 transition-colors',
  {
    variants: {
      state: {
        completed: 'bg-green-100 text-black',
        current: 'bg-amber-100 text-black animate-pulse',
        upcoming: 'bg-muted text-muted-foreground',
        rejected: 'bg-red-100 text-black',
        escalated: 'bg-orange-100 text-black',
        skipped: 'bg-muted text-muted-foreground line-through',
      },
      size: {
        detailed: 'h-8 w-8',
        compact: 'h-6 w-6',
        mini: 'h-4 w-4',
      },
    },
    defaultVariants: {
      state: 'upcoming',
      size: 'detailed',
    },
  }
);

const connectorVariants = cva('transition-colors', {
  variants: {
    state: {
      completed: 'bg-green-400',
      current: 'bg-amber-300',
      upcoming: 'bg-muted',
      rejected: 'bg-red-400',
      escalated: 'bg-orange-400',
      skipped: 'bg-muted',
    },
    variant: {
      detailed: 'w-0.5 h-4 mx-auto',
      compact: 'h-0.5 w-4',
      mini: 'h-0.5 w-2',
    },
  },
  defaultVariants: {
    state: 'upcoming',
    variant: 'detailed',
  },
});

// --- Sub-components ---

interface StepIconProps {
  state: ResolvedStepState;
  size: 'detailed' | 'compact' | 'mini';
}

function StepIcon({ state, size }: StepIconProps) {
  const iconSize = size === 'detailed' ? 16 : size === 'compact' ? 14 : 10;

  switch (state) {
    case 'completed':
      return <Check size={iconSize} strokeWidth={3} />;
    case 'rejected':
      return <X size={iconSize} strokeWidth={3} />;
    case 'current':
      return <Clock size={iconSize} />;
    case 'escalated':
      return <AlertTriangle size={iconSize} />;
    case 'skipped':
      return <Check size={iconSize} className="opacity-50" />;
    case 'upcoming':
    default:
      return <span className={cn(
        'rounded-full bg-muted-foreground/30',
        size === 'detailed' ? 'h-2 w-2' : size === 'compact' ? 'h-1.5 w-1.5' : 'h-1 w-1'
      )} />;
  }
}

// --- Main component ---

export interface WorkflowProgressTrackerProps extends VariantProps<typeof trackerVariants> {
  workflow: WorkflowInstanceView;
  className?: string;
}

export function WorkflowProgressTracker({
  workflow,
  variant = 'detailed',
  className,
}: WorkflowProgressTrackerProps) {
  const { t } = useTranslation(['leaves', 'common']);
  const resolvedVariant = variant ?? 'detailed';
  const steps = [...workflow.steps].sort((a, b) => a.stepOrder - b.stepOrder);
  const currentStep = steps.find(s => s.stepOrder === workflow.currentStepOrder);
  const currentIndex = currentStep ? steps.indexOf(currentStep) + 1 : steps.length;
  const totalSteps = steps.length;

  return (
    <div
      className={cn(trackerVariants({ variant: resolvedVariant }), className)}
      role="progressbar"
      aria-label={t('workflowProgressLabel', {
        current: currentIndex,
        total: totalSteps,
        defaultValue: `Approval progress: Step ${currentIndex} of ${totalSteps}`,
      })}
      aria-valuenow={currentIndex}
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      data-testid="workflow-progress-tracker"
    >
      {steps.map((step, index) => {
        const state = resolveStepState(step, workflow.currentStepOrder);
        const isLast = index === steps.length - 1;

        if (resolvedVariant === 'detailed') {
          return (
            <React.Fragment key={step.id}>
              <div className="flex items-start gap-3" data-testid={`workflow-step-${step.stepOrder}`}>
                <div className="flex flex-col items-center">
                  <div className={stepIndicatorVariants({ state, size: 'detailed' })}>
                    <StepIcon state={state} size="detailed" />
                  </div>
                  {!isLast && (
                    <div className={connectorVariants({ state, variant: 'detailed' })} />
                  )}
                </div>
                <div className="flex-1 min-w-0 pb-2">
                  <p className="text-sm font-medium text-foreground">
                    {step.approverName || t('workflowStepApprover', {
                      step: step.stepOrder,
                      defaultValue: `Step ${step.stepOrder}`,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(stepStatusKey(state))}
                    {step.actionDate && (
                      <span className="ms-1">
                        &middot; {new Date(step.actionDate).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                  {step.comments && (
                    <p className="text-xs text-muted-foreground mt-0.5 italic truncate">
                      {step.comments}
                    </p>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        }

        // Compact and mini variants
        const size = resolvedVariant === 'compact' ? 'compact' : 'mini';
        return (
          <React.Fragment key={step.id}>
            <div
              className={stepIndicatorVariants({ state, size })}
              title={step.approverName || t('workflowStepApprover', { step: step.stepOrder, defaultValue: `Step ${step.stepOrder}` })}
              data-testid={`workflow-step-${step.stepOrder}`}
            >
              <StepIcon state={state} size={size} />
            </div>
            {!isLast && (
              <div className={connectorVariants({ state, variant: resolvedVariant })} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// --- Helpers ---

function stepStatusKey(state: ResolvedStepState): string {
  switch (state) {
    case 'completed': return 'Approved';
    case 'current': return 'Pending';
    case 'rejected': return 'Rejected';
    case 'escalated': return 'workflowEscalated';
    case 'skipped': return 'workflowSkipped';
    case 'upcoming': return 'workflowUpcoming';
  }
}

export default WorkflowProgressTracker;
